import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type Language = "en" | "ar";
type Direction = "ltr" | "rtl";

type LanguageContextType = {
	language: Language;
	direction: Direction;
	setLanguage: (lang: Language) => void;
	toggleLanguage: () => void;
	t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(
	undefined,
);

const LANG_STORAGE_KEY = "designs-by-maryam-lang";

// Translation dictionary
const translations: Record<Language, Record<string, string>> = {
	en: {
		// Navigation
		home: "Home",
		portfolio: "Portfolio",
		about: "About",
		contact: "Contact",
		login: "Login",
		logout: "Logout",

		// Hero
		"hero.badge": "CRPEP Licensed Interior Architect",
		"hero.headline": "Crafting Exceptional Interiors",
		"hero.subtitle": "Transforming Spaces with Elegant Interior Design",
		"hero.bio":
			"I'm an interior designer passionate about creating spaces that reflect the warmth and sophistication of Bahraini culture.",
		"hero.cta.portfolio": "View Portfolio",
		"hero.cta.contact": "Book Consultation",

		// Stats
		"stats.projects": "Projects Completed",
		"stats.years": "Years Experience",
		"stats.satisfaction": "Client Satisfaction",

		// Footer
		"footer.tagline": "Transforming spaces into extraordinary experiences",
		"footer.quicklinks": "Quick Links",
		"footer.credentials": "Credentials",
		"footer.contact": "Contact",
		"footer.license": "CRPEP Licensed (Category B, October 2023)",
		"footer.education": "B.Sc. Interior Design, University of Bahrain",
		"footer.privacy": "Privacy Policy",
		"footer.terms": "Terms of Service",

		// Theme
		"theme.light": "Light",
		"theme.dark": "Dark",

		// Language
		"lang.english": "English",
		"lang.arabic": "Arabic",
	},
	ar: {
		// Navigation
		home: "الرئيسية",
		portfolio: "المشاريع",
		about: "عن مريم",
		contact: "تواصل",
		login: "دخول",
		logout: "خروج",

		// Hero
		"hero.badge": "مهندسة معمارية داخلية مرخصة",
		"hero.headline": "تصميم داخلي استثنائي",
		"hero.subtitle": "نحول المساحات إلى تجارب أنيقة",
		"hero.bio":
			"أنا مصممة داخلية شغوفة بإنشاء مساحات تعكس دفء وأناقة الثقافة البحرينية.",
		"hero.cta.portfolio": "عرض المشاريع",
		"hero.cta.contact": "احجز استشارة",

		// Stats
		"stats.projects": "مشروع مكتمل",
		"stats.years": "سنوات خبرة",
		"stats.satisfaction": "رضا العملاء",

		// Footer
		"footer.tagline": "نحول المساحات إلى تجارب استثنائية",
		"footer.quicklinks": "روابط سريعة",
		"footer.credentials": "المؤهلات",
		"footer.contact": "تواصل",
		"footer.license": "مرخصة CRPEP (فئة ب، أكتوبر 2023)",
		"footer.education": "بكالوريوس التصميم الداخلي، جامعة البحرين",
		"footer.privacy": "سياسة الخصوصية",
		"footer.terms": "شروط الخدمة",

		// Theme
		"theme.light": "فاتح",
		"theme.dark": "داكن",

		// Language
		"lang.english": "English",
		"lang.arabic": "العربية",
	},
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
	const [language, setLanguageState] = useState<Language>("en");

	// Initialize language from localStorage
	useEffect(() => {
		try {
			const stored = localStorage.getItem(LANG_STORAGE_KEY) as Language | null;
			if (stored && ["en", "ar"].includes(stored)) {
				setLanguageState(stored);
			}
		} catch {
			// localStorage may be unavailable in private browsing
		}
	}, []);

	// Apply direction to document
	useEffect(() => {
		const dir = language === "ar" ? "rtl" : "ltr";
		document.documentElement.dir = dir;
		document.documentElement.lang = language;
	}, [language]);

	const direction: Direction = language === "ar" ? "rtl" : "ltr";

	const setLanguage = useCallback((lang: Language) => {
		setLanguageState(lang);
		try {
			localStorage.setItem(LANG_STORAGE_KEY, lang);
		} catch {
			// localStorage may be unavailable in private browsing
		}
	}, []);

	const toggleLanguage = useCallback(() => {
		setLanguage(language === "en" ? "ar" : "en");
	}, [language, setLanguage]);

	const t = useCallback(
		(key: string): string => translations[language][key] || key,
		[language],
	);

	return (
		<LanguageContext.Provider
			value={{ language, direction, setLanguage, toggleLanguage, t }}
		>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error("useLanguage must be used within a LanguageProvider");
	}
	return context;
}
